import { useState } from "react";
import { NavLink, useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ROLE_LABELS } from "@/lib/api";
import {
    Gauge,
    Car,
    ClipboardText,
    Wrench,
    Bell,
    FileText,
    Users,
    ShieldCheck,
    Handshake,
    GearSix,
    SignOut,
    Envelope,
    Trophy,
    MapPin,
    Brain,
    EnvelopeSimple,
    CreditCard,
    Crown,
    CalendarBlank,
    Buildings,
    Palette,
    Bank,
    Image as ImageIcon,
    Receipt,
    Clock,
    ListDashes,
    SteeringWheel,
    GasPump,
} from "@phosphor-icons/react";
import QuickLinksModal from "./QuickLinksModal";
import InstallAppButton from "./InstallAppButton";

const NAV = [
    { to: "/dashboard", label: "ダッシュボード", icon: Gauge, testid: "nav-dashboard", tenantOnly: true },
    { to: "/vehicles", label: "車両台帳", icon: Car, testid: "nav-vehicles", perm: "vehicles.view", tenantOnly: true },
    { to: "/inspections", label: "点検履歴", icon: ClipboardText, testid: "nav-inspections", perm: "inspections.view", tenantOnly: true },
    { to: "/inspect", label: "使用前点検（現場）", icon: ShieldCheck, testid: "nav-inspect", perm: "inspections.perform", tenantOnly: true },
    { to: "/maintenance", label: "整備管理", icon: Wrench, testid: "nav-maintenance", perm: "maintenance.view", tenantOnly: true },
    { to: "/operations", label: "運行記録", icon: SteeringWheel, testid: "nav-operations", perm: "operations.view", tenantOnly: true },
    { to: "/fuel", label: "燃料記録", icon: GasPump, testid: "nav-fuel", perm: "fuel.view", tenantOnly: true },
    { to: "/partners", label: "整備工場", icon: Handshake, testid: "nav-partners", perm: "partners.view", tenantOnly: true },
    { to: "/notifications", label: "通知センター", icon: Bell, testid: "nav-notifications", perm: "notifications.send", tenantOnly: true },
    { to: "/mailbox", label: "通知ログ", icon: Envelope, testid: "nav-mailbox", perm: "notifications.view", tenantOnly: true },
    { to: "/calendar", label: "カレンダー", icon: CalendarBlank, testid: "nav-calendar", perm: "calendar.view", tenantOnly: true },
    { to: "/webmail", label: "WEBメール", icon: EnvelopeSimple, testid: "nav-webmail", perm: "webmail.view", tenantOnly: true },
    { to: "/reports", label: "帳票出力", icon: FileText, testid: "nav-reports", perm: "reports.generate", tenantOnly: true },
    { to: "/driver-scores", label: "ドライバー評価", icon: Trophy, testid: "nav-driver-scores", perm: "driver_scores.view", tenantOnly: true },
    { to: "/department-heatmap", label: "部署別分析", icon: Buildings, testid: "nav-dept-heatmap", perm: "driver_scores.view", tenantOnly: true },
    { to: "/gps", label: "GPS トラッキング", icon: MapPin, testid: "nav-gps", perm: "gps.view", tenantOnly: true },
    { to: "/users", label: "ユーザー・権限", icon: Users, testid: "nav-users", perm: "users.view", tenantOnly: true },
    { to: "/billing", label: "プラン・請求", icon: CreditCard, testid: "nav-billing", perm: "company.billing", tenantOnly: true },
    { to: "/audit", label: "監査ログ", icon: ShieldCheck, testid: "nav-audit", perm: "company.audit.view", tenantOnly: true },
    { to: "/settings", label: "会社設定", icon: GearSix, testid: "nav-settings", perm: "company.settings.view", tenantOnly: true },
    { to: "/admin", label: "Super Admin", icon: Crown, testid: "nav-super-admin", perm: "admin.view" },
];

// Super Admin (運営) 専用の縦メニュー項目 — サイドバーに個別展開
const SUPER_ADMIN_NAV = [
    { to: "/admin?tab=overview",  label: "概要",         icon: Gauge,      testid: "nav-sa-overview" },
    { to: "/admin?tab=tenants",   label: "テナント管理",  icon: Buildings,  testid: "nav-sa-tenants",   alertKey: "tenants" },
    { to: "/admin?tab=operator",  label: "運営法人設定",  icon: Palette,    testid: "nav-sa-operator",  alertKey: "operator" },
    { to: "/admin?tab=banking",   label: "振込先",       icon: Bank,       testid: "nav-sa-banking",   alertKey: "banking" },
    { to: "/admin?tab=landing",   label: "フロントCMS",   icon: ImageIcon,  testid: "nav-sa-landing" },
    { to: "/admin?tab=quicklinks",label: "クイックリンクCMS", icon: ListDashes, testid: "nav-sa-quicklinks" },
    { to: "/admin?tab=invoices",  label: "請求管理",      icon: Receipt,    testid: "nav-sa-invoices",  alertKey: "invoices" },
    { to: "/admin?tab=scheduler", label: "スケジューラ",  icon: Clock,      testid: "nav-sa-scheduler", alertKey: "scheduler", severe: true },
];

export default function AppShell({ children }) {
    const { user, logout, can, branding, opsAlerts } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const isSuperAdmin = user?.role === "super_admin";
    const currentTab = new URLSearchParams(location.search).get("tab") || "overview";
    const eff = branding?.effective || {};
    const [qlOpen, setQlOpen] = useState(false);
    const brandName = eff.brand_name || "PRC CMC";
    const brandSub = eff.brand_sub || "FLEET CONTROL OS";
    const accent = eff.accent_color || "#00ff9c";
    const logoUrl = eff.logo_data_url;

    const handleLogout = async () => {
        await logout();
        navigate("/login");
    };

    return (
        <div className="min-h-screen flex bg-[var(--prc-bg)]">
            {/* Sidebar */}
            <aside className="hidden lg:flex w-64 flex-col border-r border-[var(--prc-border)] bg-[var(--prc-surface)]" data-testid="sidebar">
                <div className="px-5 py-5 border-b border-[var(--prc-border)]">
                    <Link to={isSuperAdmin ? "/admin" : "/dashboard"} className="block" data-testid="brand-link" style={{ visibility: branding ? "visible" : "hidden" }}>
                        <div className="flex items-center gap-2">
                            {logoUrl ? (
                                <img src={logoUrl} alt="logo" className="h-6 w-6 object-contain" data-testid="sidebar-logo" />
                            ) : (
                                <span className="w-2 h-2 rounded-full" style={{ background: accent, boxShadow: `0 0 8px ${accent}` }} />
                            )}
                            <p className="font-mono text-[16px] font-extrabold tracking-[0.08em] text-[var(--prc-ink-2)] truncate" data-testid="sidebar-brand-name">
                                {brandName}
                            </p>
                        </div>
                        <p className="prc-label-overline mt-1.5" data-testid="sidebar-brand-sub">{brandSub}</p>
                        {branding?.effective?.source === "tenant" && !eff.hide_powered_by && (
                            <p className="mt-1 text-[9px] text-[var(--prc-tertiary)] font-mono uppercase tracking-wider">Powered by PRC CMC</p>
                        )}
                    </Link>
                </div>
                <nav className="flex-1 py-3">
                    {isSuperAdmin
                        ? SUPER_ADMIN_NAV.map((n) => {
                            const tabKey = new URLSearchParams(n.to.split("?")[1] || "").get("tab");
                            const active = location.pathname === "/admin" && currentTab === tabKey;
                            const count = n.alertKey ? (opsAlerts?.counts?.[n.alertKey] || 0) : 0;
                            return (
                                <Link
                                    key={n.to}
                                    to={n.to}
                                    data-testid={n.testid}
                                    className={`prc-sidebar-link ${active ? "active" : ""}`}
                                >
                                    <n.icon size={24} weight="regular" />
                                    <span className="flex-1">{n.label}</span>
                                    {count > 0 && (
                                        <span
                                            className="ml-2 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-sm text-[10px] font-mono font-bold"
                                            style={{
                                                background: n.severe ? "var(--prc-red)" : "var(--prc-green)",
                                                color: n.severe ? "#fff" : "#04110b",
                                            }}
                                            data-testid={`${n.testid}-badge`}
                                        >
                                            {count > 99 ? "99+" : count}
                                        </span>
                                    )}
                                </Link>
                            );
                          })
                        : NAV
                            .filter((n) => !n.perm || can(n.perm))
                            .map((n) => (
                                <NavLink
                                    key={n.to}
                                    to={n.to}
                                    data-testid={n.testid}
                                    className={({ isActive }) =>
                                        `prc-sidebar-link ${isActive ? "active" : ""}`
                                    }
                                >
                                    <n.icon size={24} weight="regular" />
                                    <span>{n.label}</span>
                                </NavLink>
                            ))}
                </nav>
                <div className="border-t border-[var(--prc-border)] p-4 text-xs text-[var(--prc-muted)]">
                    <InstallAppButton testid="sidebar-install-app-btn" className="w-full justify-center text-xs mb-3" />
                    <p className="prc-label-overline mb-1">{ROLE_LABELS[user?.role] || user?.role}</p>
                    <p className="truncate font-medium text-[var(--prc-ink-2)]" data-testid="sidebar-user-name">
                        {user?.name}
                    </p>
                    <p className="truncate" data-testid="sidebar-company">{user?.company_name}</p>
                    <div className="mt-3 flex items-center gap-3">
                        <button
                            onClick={handleLogout}
                            className="flex items-center gap-2 text-[var(--prc-muted)] hover:text-[var(--prc-red)] transition-colors font-mono text-[11px] tracking-wider uppercase"
                            data-testid="logout-btn"
                        >
                            <SignOut size={16} />
                            <span>LOGOUT</span>
                        </button>
                        <button
                            onClick={() => setQlOpen(true)}
                            className="flex items-center gap-1.5 text-[var(--prc-muted)] hover:text-[var(--prc-green)] transition-colors font-mono text-[11px] tracking-wider uppercase"
                            data-testid="sidebar-quick-links-btn"
                        >
                            <ListDashes size={16} />
                            <span>LINKS</span>
                        </button>
                    </div>
                </div>
            </aside>

            {/* Main */}
            <main className="flex-1 min-w-0 flex flex-col">
                {/* Header (mobile shows hamburger-like nav simplified) */}
                <header className="lg:hidden border-b border-[var(--prc-border)] bg-[var(--prc-surface)] px-4 py-3 flex justify-between items-center">
                    <Link to={isSuperAdmin ? "/admin" : "/dashboard"} className="font-mono font-extrabold text-sm tracking-[0.1em] text-[var(--prc-ink-2)] flex items-center gap-2" style={{ visibility: branding ? "visible" : "hidden" }}>
                        {logoUrl ? (
                            <img src={logoUrl} alt="logo" className="h-5 w-5 object-contain" />
                        ) : (
                            <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: accent, boxShadow: `0 0 6px ${accent}` }} />
                        )}
                        {brandName}
                    </Link>
                    <div className="flex items-center gap-2">
                        <InstallAppButton testid="mobile-install-app-btn" className="!px-2 !py-1 text-[10px]" />
                        <button onClick={handleLogout} data-testid="logout-btn-mobile" className="text-[10px] font-mono tracking-wider uppercase text-[var(--prc-muted)]">
                            LOGOUT
                        </button>
                    </div>
                </header>
                {/* Mobile bottom nav (primary routes) */}
                <div className="lg:hidden fixed bottom-0 inset-x-0 z-40 border-t border-[var(--prc-border)] bg-[var(--prc-surface)] flex" data-testid="mobile-bottom-nav">
                    {(() => {
                        if (isSuperAdmin) return [{ to: "/admin", label: "運営", Icon: Crown }];
                        if (user?.role === "staff") {
                            // Staff-tailored bottom nav: only the actions a field
                            // worker actually does on a phone, no admin clutter.
                            return [
                                { to: "/dashboard",     label: "ホーム",   Icon: Gauge },
                                { to: "/inspect",       label: "点検",     Icon: ShieldCheck },
                                { to: "/operations",    label: "運行",     Icon: SteeringWheel },
                                { to: "/fuel",          label: "給油",     Icon: GasPump },
                                { to: "/notifications", label: "通知",     Icon: Bell },
                            ];
                        }
                        return [
                            { to: "/dashboard",     label: "ダッシュボード", Icon: Gauge },
                            { to: "/vehicles",      label: "車両",          Icon: Car },
                            { to: "/inspect",       label: "点検",          Icon: ShieldCheck },
                            { to: "/notifications", label: "通知",          Icon: Bell },
                            { to: "/reports",       label: "帳票",          Icon: FileText },
                        ];
                    })().map((n) => (
                        <NavLink
                            key={n.to}
                            to={n.to}
                            className={({ isActive }) =>
                                `flex-1 flex flex-col items-center py-2 text-[10px] ${isActive ? "text-[var(--prc-ink)] font-semibold" : "text-[var(--prc-muted)]"}`
                            }
                        >
                            <n.Icon size={24} />
                            <span>{n.label}</span>
                        </NavLink>
                    ))}
                </div>
                <div className="flex-1 p-4 lg:p-8 pb-24 lg:pb-8">{children}</div>
            </main>
            <QuickLinksModal open={qlOpen} onClose={() => setQlOpen(false)} />
        </div>
    );
}
